import ScreenWrapper from "@/components/ScreenWrapper"
import {
  DataTable,
  createCustomColumn,
  selectColumn,
  createSortableColumn,
  getFullName,
  TABLE_PAGE_SIZE,
  usePaginatedTableWithUrlState,
  createTableSearchSchema,
  StudentOrganizationUserConnectionModal,
  MAX_PAGINATED_PAGE_SIZE,
  Filters,
  createActionsColumn,
} from "toptaal-shared"
import type { ColumnDef } from "@tanstack/react-table"
import {
  createFileRoute,
  useNavigate,
  useRouter,
  useSearch,
} from "@tanstack/react-router"
import { fetchFilteredStudents } from "@/api/services/student"
import { studentQueryKeys } from "@/api/query-keys/student"
import { Student } from "@/api/types/student"
import { useStudentCreateStore } from "@/stores/useStudentCreateStore"
import { TableHeaderActions } from "@/components/TableHeaderActions"
import { useState } from "react"
import { useOrganizationUsersSuspenseQuery } from "@/api/queries/organizationUser/useOrganizationUsersSuspenseQuery"
import { useConnectStudentOrganizationUsersBulkMutation } from "@/api/mutations/organizations/useConnectStudentOrganizationUsersBulkMutation"

export const Route = createFileRoute("/beheer/cursisten/")({
  component: RouteComponent,
  validateSearch: createTableSearchSchema(),
})

const DEFAULT_COLUMN_VISIBILITY = {
  postalCode: false,
}

function RouteComponent() {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedStudent, setSelectedStudent] = useState<Student | null>(null)
  const searchParams = useSearch({ from: "/beheer/cursisten/" })
  const navigate = useNavigate({
    from: "/beheer/cursisten/",
  })

  const connectStudentOrganizationUsersBulkMutation =
    useConnectStudentOrganizationUsersBulkMutation()

  const filters: Filters = {
    pageSize: MAX_PAGINATED_PAGE_SIZE,
    page: 1,
  }
  const router = useRouter()
  const { clearStudentCreateStore } = useStudentCreateStore()

  const {
    isFetching,
    data,
    search,
    setSearch,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,
    pagination,
    columnVisibility,
    setColumnVisibility,
    resetColumnVisibility,
  } = usePaginatedTableWithUrlState({
    queryKey: studentQueryKeys.all,
    queryFn: fetchFilteredStudents,
    pageSize: TABLE_PAGE_SIZE,
    searchParams,
    navigate,
    storageKey: "/beheer/cursisten",
    defaultColumnVisibility: DEFAULT_COLUMN_VISIBILITY,
  })

  const handleNavigateToStudentDetail = (studentId: Student["id"]) => {
    router.navigate({
      to: "/beheer/cursisten/$studentId/gegevens",
      params: { studentId: studentId.toString() },
    })
  }

  const columns: ColumnDef<Student>[] = [
    selectColumn(),
    createSortableColumn({ accessorKey: "id", name: "ID" }),
    createCustomColumn({
      id: "firstName",
      name: "Naam",
      cell: ({ row }: { row: { original: Student } }) =>
        getFullName({
          firstName: row.original.firstName,
          lastName: row.original.lastName,
        }),
      accessorKey: "firstName",
    }),
    createSortableColumn({ accessorKey: "email", name: "E-mailadres" }),
    createSortableColumn({
      accessorKey: "phoneNumberCall",
      name: "Telefoonnummer",
    }),
    createSortableColumn({ accessorKey: "city", name: "Woonplaats" }),
    createSortableColumn({ accessorKey: "postalCode", name: "Postcode" }),
    createActionsColumn([
      {
        label: "Koppelen aan contactpersoon",
        onClick: (e, student) => {
          e.stopPropagation()
          setIsOpen(true)
          setSelectedStudent(student)
        },
      },
    ]),
  ]

  return (
    <ScreenWrapper showManagementMenu>
      <DataTable
        title="Cursisten"
        data={data?.data.students || []}
        isLoading={isFetching}
        pagination={pagination}
        sortDirection={sortDirection}
        sortField={sortField}
        search={search}
        setSearch={setSearch}
        setSortField={setSortField}
        setSortDirection={setSortDirection}
        columns={columns}
        columnVisibility={columnVisibility}
        setColumnVisibility={setColumnVisibility}
        resetColumnVisibility={resetColumnVisibility}
        onRowClick={(studentId) => handleNavigateToStudentDetail(studentId)}
        headerRightActions={
          <TableHeaderActions
            createAction={{
              label: "Nieuwe cursist",
              entity: "students",
              permissions: "create",
              onClick: () => {
                clearStudentCreateStore()
                navigate({
                  to: "/beheer/cursisten/aanmaken",
                })
              },
            }}
          />
        }
      />

      {isOpen && (
        <StudentOrganizationUserConnectionModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          connectStudentOrganizationUsersBulkMutation={
            connectStudentOrganizationUsersBulkMutation
          }
          studentQueryKey={studentQueryKeys.filters(filters)}
          useOrganizationSuspenseQuery={useOrganizationUsersSuspenseQuery}
          filters={filters}
          student={selectedStudent}
        />
      )}
    </ScreenWrapper>
  )
}
